// @flow

import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type OutgoingPaymentMapper } from './types';

import { captchaOutgoingPaymentMapper } from '@tectonics/captcha';
import { metadataOutgoingPaymentMapper } from '../../metadata';
import { fingerprintOutgoingPaymentMapper } from '../../fingerprint';
import { customerOutgoingPaymentMapper } from '../../customer';
import { mexicanOutgoingPaymentMapper } from '../../mexico';
import { peruvianOutgoingPaymentMapper } from '../../peru';
import { brazilianOutgoingPaymentMapper } from '../../brazil';
import { argentineOutgoingPaymentMapper } from '../../argentina';
import { uruguayanOutgoingPaymentMapper } from '../../uruguay';
import { colombianOutgoingPaymentMapper } from '../../colombia';
import { chileanOutgoingPaymentMapper } from '../../chile';
import { ecuadorianOutgoingPaymentMapper } from '../../ecuador';
import { bolivianOutgoingPaymentMapper } from '../../bolivia';
import { threeDSecureOutgoingPaymentMapper } from '../../threedsecure';

import { tefOutgoingPaymentMapper } from '../../tef';
import { oxxoOutgoingPaymentMapper } from '../../oxxo';
import { oxxopayOutgoingPaymentMapper } from '../../oxxopay';
import { speiOutgoingPaymentMapper } from '../../spei';
import { boletoOutgoingPaymentMapper } from '../../boleto';
import { googlePayOutgoingPaymentMapper } from '../../google-pay';
import { creditCardOutgoingPaymentMapper } from '../../credit-card';
import { debitCardOutgoingPaymentMapper } from '../../debit-card';
import { bankTransferOutgoingPaymentMapper } from '../../banktransfer';
import { balotoOutgoingPaymentMapper } from '../../baloto';
import { multicajaOutgoingPaymentMapper } from '../../multicaja';
import { servipagOutgoingPaymentMapper } from '../../servipag';
import { eftColombiaOutgoingPaymentMapper } from '../../eft';
import { sencillitoOutgoingPaymentMapper } from '../../sencillito';
import { webpayOutgoingPaymentMapper } from '../../webpay';
import { pagoefectivoOutgoingPaymentMapper } from '../../pagoefectivo';
import { safetypayOutgoingPaymentMapper } from '../../safetypay';
import { cashMexicoOutgoingPaymentMapper } from '../../cash-mexico';
import { cashBoliviaOutgoingPaymentMapper } from '../../cash-bolivia';
import { safetypayCashOutgoingPaymentMapper } from '../../safetypay-cash';
import { safetypayOnlineOutgoingPaymentMapper } from '../../safetypay-online';
import { argentineCashOutgoingPaymentMapper } from '../../cash-argentina';
import { ewalletOutgoingPaymentMapper } from '../../ewallet';
import { efectyOutgoingPaymentMapper } from '../../efecty';
import { pixOutgoingPaymentMapper } from '../../pix';

import { type Country } from '@tectonics/prs/country';

export function createOutgoingPaymentMappers(userInputValues: UserInputValues, country: Country): ImmutableArray<OutgoingPaymentMapper> {
  const selectedPaymentMethod = userInputValues['selectedPaymentMethod'];

  return [
    metadataOutgoingPaymentMapper,
    fingerprintOutgoingPaymentMapper,
    captchaOutgoingPaymentMapper,
    customerOutgoingPaymentMapper,
    getCountryMapper(country),
    getPaymentMethodMapper(selectedPaymentMethod, country),
    threeDSecureOutgoingPaymentMapper,
  ];
}

function getPaymentMethodMapper(selectedPaymentMethod: string, country: Country): OutgoingPaymentMapper {
  switch (selectedPaymentMethod) {
    case 'creditcard':
      return creditCardOutgoingPaymentMapper;
    case 'debitcard':
      return debitCardOutgoingPaymentMapper;
    case 'boleto':
      return boletoOutgoingPaymentMapper;
    case 'banktransfer':
      return bankTransferOutgoingPaymentMapper;
    case 'tef':
      return tefOutgoingPaymentMapper;
    case 'oxxo':
      return oxxoOutgoingPaymentMapper;
    case 'oxxopay':
      return oxxopayOutgoingPaymentMapper;
    case 'spei':
      return speiOutgoingPaymentMapper;
    case 'cash':
      return cashOutgoingMapperResolver(country);
    case 'google':
      return googlePayOutgoingPaymentMapper;
    case 'baloto':
      return balotoOutgoingPaymentMapper;
    case 'multicaja':
      return multicajaOutgoingPaymentMapper;
    case 'servipag':
      return servipagOutgoingPaymentMapper;
    case 'eft':
      return eftColombiaOutgoingPaymentMapper;
    case 'sencillito':
      return sencillitoOutgoingPaymentMapper;
    case 'webpay':
      return webpayOutgoingPaymentMapper;
    case 'pagoefectivo':
      return pagoefectivoOutgoingPaymentMapper;
    case 'safetypay':
      return safetypayOutgoingPaymentMapper;
    case 'safetypay-cash':
      return safetypayCashOutgoingPaymentMapper;
    case 'safetypay-online':
      return safetypayOnlineOutgoingPaymentMapper;
    case 'ewallet':
      return ewalletOutgoingPaymentMapper;
    case 'pix':
      return pixOutgoingPaymentMapper;
    default:
      throw new Error(`outgoing payment mapper: ${selectedPaymentMethod} not mapped`);
  }
}

function cashOutgoingMapperResolver(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'mx':
      return cashMexicoOutgoingPaymentMapper;
    case 'bo':
      return cashBoliviaOutgoingPaymentMapper;
    case 'ar':
      return argentineCashOutgoingPaymentMapper;
    case 'co':
      return efectyOutgoingPaymentMapper;
    default:
      throw new Error(`outgoing payment mapper for method cash in country: ${country} not found`);

  }
}

function getCountryMapper(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'br':
      return brazilianOutgoingPaymentMapper;
    case 'ar':
      return argentineOutgoingPaymentMapper;
    case 'bo':
      return bolivianOutgoingPaymentMapper;
    case 'cl':
      return chileanOutgoingPaymentMapper;
    case 'co':
      return colombianOutgoingPaymentMapper;
    case 'ec':
      return ecuadorianOutgoingPaymentMapper;
    case 'mx':
      return mexicanOutgoingPaymentMapper;
    case 'pe':
      return peruvianOutgoingPaymentMapper;
    case 'uy':
      return uruguayanOutgoingPaymentMapper;
    default:
      throw new Error(`No country mapper for country ${country}`);
  }
}
